/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import classNames from 'classnames';
import gql from 'graphql-tag';
import { compact, sortBy } from 'lodash';
import { compose } from 'recompose';

import mapProps from 'js/lib/mapProps';

import { Typography } from '@coursera/cds-core';

import { renderToggleButtonContentWithContext } from 'bundles/enterprise-legacy-xdp/components/aboutXrenderFuncs';
import withFragments from 'bundles/graphql/components/withFragments';
import { isSpark, sortCourses } from 'bundles/graphql/utils/CoursesV1';
import TogglableContent from 'bundles/page/components/TogglableContent';
import Course from 'bundles/partners-graphql/components/Course';
import Specialization from 'bundles/partners-graphql/components/Specialization';
import type {
  PartnerPageQuery_PartnersV1Resource_shortName_partners_courses_elements as CourseData,
  PartnerPageQuery_PartnersV1Resource_shortName_partners_courses as Courses,
  PartnerPageQuery_PartnersV1Resource_shortName_partners_specializations_elements as S12nData,
  PartnerPageQuery_PartnersV1Resource_shortName_partners_specializations as Specializations,
} from 'bundles/partners-graphql/components/__generated__/PartnerPageQuery';
import PRODUCT_VARIANT from 'bundles/s12n-common/constants/s12nProductVariants';

import _t from 'i18n!nls/partners-graphql';

type PropsFromCaller = {
  title: string;
  partnerShortName: string;
  sortCoursesAlphabetically: boolean;
  courses: Courses | null;
  specializations: Specializations | null;
};

export type S12nDataWithLaunchedAtDefined = Omit<S12nData, 'launchedAt'> & {
  launchedAt: number;
};

type SanitizedCoursesAndS12ns = {
  courses: CourseData[];
  specializations: S12nDataWithLaunchedAtDefined[];
};

type PropsToComponent = Omit<PropsFromCaller, 'courses' | 'specializations'> & SanitizedCoursesAndS12ns;

const styles = {
  courseHeader: css`
    margin: 16px !important;
  `,
  togglableContent: css`
    .collapse-button {
      margin-left: 15px !important;
    }
  `,
};

const Cards = ({ courses, sortCoursesAlphabetically, specializations, title, partnerShortName }: PropsToComponent) => {
  // Find non spark and standalone courses
  const nonSparkCourses = courses.filter((course) => {
    return !isSpark(course);
  });

  const uniqueSpecialisations = specializations.filter(
    (specialization, index, array) => array.findIndex((s12n) => s12n.id === specialization.id) === index
  );

  const sortedS12ns = sortBy(uniqueSpecialisations, (s12n) => s12n.name);

  let sortedCourses;
  if (sortCoursesAlphabetically) {
    sortedCourses = sortBy(nonSparkCourses, (course) => course.name);
  } else {
    sortedCourses = sortCourses(nonSparkCourses, { boostPrimaryLanguage: true });
  }

  return (
    <div className="bt3-row">
      <div className="bt3-col-md-12">
        {title && (
          <Typography variant="h2" component="h3" css={styles.courseHeader}>
            {title}
          </Typography>
        )}
        {sortedS12ns && sortedS12ns.map((s12n) => <Specialization key={s12n.id} s12n={s12n} />)}
        <TogglableContent
          css={styles.togglableContent}
          collapsedHeight={340}
          disableCollapseScroll={true}
          hideShowLessBtn={true}
          renderToggleButtonContentFunction={renderToggleButtonContentWithContext(_t('Show All'))}
          toggleButtonClassName={classNames('bold m-t-1', 'collapse-button')}
          toggleButtonType="button"
          trackingName="show-all-partner-courses"
          useSmartCollapse
        >
          {sortedCourses.map((course) => (
            <Course key={course.id} partnerShortName={partnerShortName} course={course} />
          ))}
        </TogglableContent>
      </div>
    </div>
  );
};

export const coursesFragment = gql`
  fragment CoursesFragment on CoursesV1Connection {
    elements {
      id
      name
      ...CoursesV1_isSpark
      ...CoursesV1_sortCourses
      ...PartnersCourseFragment
    }
  }
  ${Course.fragments.courseFragment}
  ${isSpark.fragment}
  ${sortCourses.fragment}
`;

export const s12nFragment = gql`
  fragment SpecializationsFragment on OnDemandSpecializationsV1Connection {
    elements {
      ...SpecializationFragment
    }
  }
  ${Specialization.fragments.specializationFragment}
`;

export default compose<PropsToComponent, PropsFromCaller>(
  withFragments({
    courses: coursesFragment,
    specializations: s12nFragment,
  }),
  mapProps<PropsToComponent, PropsFromCaller>(({ courses, specializations, ...rest }) => ({
    courses: compact(courses?.elements),
    specializations: compact(specializations?.elements)
      // REMARK: Only show launched, non-professional certificate s12ns.
      // The launch filter is because unlike the `CoursesV1` resource, the s12n resource
      // includes unlaunched s12ns when the requestor is a S/U or staff.
      // See https://coursera.atlassian.net/browse/GR-21466 for more details.
      // (`!!` turns null/undefined to false and any truthy value to `true`)
      .filter(
        (s12n): s12n is S12nDataWithLaunchedAtDefined =>
          s12n.productVariant !== PRODUCT_VARIANT.ProfessionalCertificateS12n && !!s12n.launchedAt
      ),
    ...rest,
  }))
)(Cards);
